




















import { Component, Vue } from "vue-property-decorator";
import { ToolbarContentModule } from "@/store/modules/ToolbarContent";
import { ComponentName } from "@/utils/RouteGuards";
import { manageClientsAccess, manageProductionAccess } from "@/permissions/UserPermissions";
import router from "@/router/router";
import SettingsItemComponent, { SettingData } from "@/components/settings/SettingsItem.vue";
// getReactiveActiveUser, not getActiveUser: this getter feeds the template, and getActiveUser()
// reads localStorage, so a role change in-session would not re-render until a page refresh.
import { getReactiveActiveUser } from "@/data/repository/UserRepository";
import { DownloadFileType, getFileTypeIcon } from "@/utils/FileTypeIcons";

/**
 * Landing page of the IRES admin portal.
 *
 * Entry point for admin functionality being migrated out of the IRES Admin Android app. Reachable
 * only by clients listed in `administration/adminConfig.officeAdminClientIds`; the gate itself is
 * applied asynchronously in `router.beforeEach`, not here, so this component must not be treated as
 * the access boundary.
 *
 * Rendered with `SettingsItem`, the same card-and-list the settings page uses, so the portal looks
 * like the rest of the app rather than a second design. Each tile shows its name only; what it is for
 * sits behind an (i) beside the name (`InfoIcon`), so the page stays a list of names. The page title comes from the app bar via
 * `ToolbarContentModule`, so there is deliberately no heading in the body: the settings page has
 * none either, and adding one printed "IRES Admin" twice.
 *
 * **This page lists only the areas that have no navigation entry of their own.** RENO
 * Schnellprüfung has one — it is where reviewers live, so making them come through here would be a
 * click for nothing — and it is therefore deliberately *not* listed here. Listing it in both places
 * was the state this page started in, and it is what made the page worth nothing.
 *
 * That leaves it empty until the device reports screens land, which is why the empty state above
 * exists and why the page's own navigation entry is gated on portal membership alone: each tile
 * carries its own capability check, so a user who holds one capability still reaches the page and
 * simply does not see another's tile.
 */
@Component({
  components: {
    "settings-item": SettingsItemComponent,
  },
})
export default class AdminPortalComponent extends Vue {
  /** One card per domain, dropping any card whose tiles are all hidden from this user. */
  get sections(): SettingData[] {
    return [this.clientsSection, this.miraSection, this.productionSection].filter(
      (section) => section.items.length > 0
    );
  }

  get clientsSection(): SettingData {
    return {
      title: this.$t("admin.categories.clients").toString(),
      items: [
        {
          // Same capability the route guard enforces; the callable behind the list checks it again.
          visible: manageClientsAccess(getReactiveActiveUser()),
          title: this.$t("admin.sections.clients.title").toString(),
          info: this.$t("admin.sections.clients.description").toString(),
          icon: "mdi-domain",
          onClickCallback: () => router.push({ name: ComponentName.CLIENT_ADMINISTRATION_LIST }),
        },
        {
          // Every external system, as IRES Admin's Externe Systeme list (#2610084, merge 3). Same gate.
          visible: manageClientsAccess(getReactiveActiveUser()),
          title: this.$t("admin.sections.externalSystems.title").toString(),
          info: this.$t("admin.sections.externalSystems.description").toString(),
          icon: "mdi-swap-horizontal",
          onClickCallback: () => router.push({ name: ComponentName.EXTERNAL_SYSTEM_LIST }),
        },
        {
          // A user across every client (2026-11-03). Same gate; the callable checks it again.
          visible: manageClientsAccess(getReactiveActiveUser()),
          title: this.$t("admin.sections.userSearch.title").toString(),
          info: this.$t("admin.sections.userSearch.description").toString(),
          icon: "mdi-account-search",
          onClickCallback: () => router.push({ name: ComponentName.ADMIN_USER_SEARCH }),
        },
        {
          // Both kinds of shared users, Bereinigen for stale "E-Mail-Adresse teilen" entries. Same gate.
          visible: manageClientsAccess(getReactiveActiveUser()),
          title: this.$t("admin.sections.sharedUsers.title").toString(),
          info: this.$t("admin.sections.sharedUsers.description").toString(),
          icon: "mdi-account-multiple",
          onClickCallback: () => router.push({ name: ComponentName.ADMIN_SHARED_USERS }),
        },
        {
          // The shared templates, as IRES Admin's Administration > PDF Vorlagen; a client's own
          // templates stay on its detail screen. Same gate and server checks as the Mandanten list.
          visible: manageClientsAccess(getReactiveActiveUser()),
          title: this.$t("admin.sections.pdfTemplates.title").toString(),
          info: this.$t("admin.sections.pdfTemplates.description").toString(),
          icon: getFileTypeIcon(DownloadFileType.PDF),
          onClickCallback: () =>
            router.push({ name: ComponentName.CLIENT_ADMINISTRATION_PDF_TEMPLATES, query: { shared: "1" } }),
        },
        {
          // The project forms of every client (#56): versions, targets, checked publishing.
          visible: manageClientsAccess(getReactiveActiveUser()),
          title: this.$t("admin.sections.infoConfig.title").toString(),
          info: this.$t("admin.sections.infoConfig.description").toString(),
          icon: "mdi-file-document-outline",
          onClickCallback: () => router.push({ name: ComponentName.INFO_CONFIG_LIST }),
        },
      ].filter((item) => item.visible),
    };
  }

  get miraSection(): SettingData {
    return {
      // Mira's reference lists (Track #2610040 P3, Taimoor 2026-10-14): the insurers and their review, and
      // the companies of the market comparison (#2610009 C4).
      // Same capability as Mandanten; the callables behind both screens check it again.
      title: this.$t("admin.categories.mira").toString(),
      items: [
        {
          visible: manageClientsAccess(getReactiveActiveUser()),
          title: this.$t("admin.sections.miraInsurers.title").toString(),
          info: this.$t("admin.sections.miraInsurers.description").toString(),
          icon: "mdi-shield-account-outline",
          onClickCallback: () => router.push({ name: ComponentName.MIRA_INSURER_LIST }),
        },
        {
          visible: manageClientsAccess(getReactiveActiveUser()),
          title: this.$t("admin.sections.miraInsurerReview.title").toString(),
          info: this.$t("admin.sections.miraInsurerReview.description").toString(),
          icon: "mdi-text-search",
          onClickCallback: () => router.push({ name: ComponentName.MIRA_INSURER_REVIEW }),
        },
        {
          // The companies of the Markt comparison (#2610009 C4): several Mandanten counted as one.
          visible: manageClientsAccess(getReactiveActiveUser()),
          title: this.$t("admin.sections.miraCompanies.title").toString(),
          info: this.$t("admin.sections.miraCompanies.description").toString(),
          icon: "mdi-office-building-outline",
          onClickCallback: () => router.push({ name: ComponentName.MIRA_COMPANY_LIST }),
        },
        {
          // Mira-Ausnahmen (Track #2610040 R8): late recordings left out of the durations.
          visible: manageClientsAccess(getReactiveActiveUser()),
          title: this.$t("admin.sections.miraExceptions.title").toString(),
          info: this.$t("admin.sections.miraExceptions.description").toString(),
          icon: "mdi-calendar-remove-outline",
          onClickCallback: () => router.push({ name: ComponentName.MIRA_EXCEPTION_LIST }),
        },
      ].filter((item) => item.visible),
    };
  }

  get productionSection(): SettingData {
    return {
      // Named for the domain it groups, not for the portal: the app bar already says "IRES Admin",
      // and repeating it here says nothing about what the card contains. Areas migrated out of the
      // Android app get their own card under their own domain name.
      title: this.$t("admin.categories.production").toString(),
      items: [
        {
          // Gated on the capability the route guard also enforces, so the tile is never offered to
          // someone the router would bounce straight back out.
          visible: manageProductionAccess(getReactiveActiveUser()),
          title: this.$t("admin.sections.deviceReports.title").toString(),
          info: this.$t("admin.sections.deviceReports.description").toString(),
          icon: "mdi-clipboard-check-outline",
          // A callback rather than `path`: SettingsItem's path form appends to the current route,
          // and these routes are addressed by name.
          onClickCallback: () => router.push({ name: ComponentName.DEVICE_REPORT_LIST }),
        },
      ].filter((item) => item.visible),
    };
  }

  mounted() {
    ToolbarContentModule.setI18nResource("admin.title");
  }
}
